import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight, Calendar, Clock } from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { POSTS, CATEGORIES, SITE_OG } from "@/data/blogPosts";
import { SITE } from "@/components/Seo";

function PostCard({ post, featured = false }) {
    const cat = CATEGORIES[post.category];
    return (
        <Link
            to={`/blog/${post.slug}`}
            className={`group block surface-strong rounded-2xl overflow-hidden hover:shadow-md transition-all hover:-translate-y-0.5 ${featured ? "md:col-span-2" : ""}`}
            data-testid={`blog-card-${post.slug}`}
        >
            <div
                className="relative h-44 md:h-48 overflow-hidden"
                style={{ background: cat.gradient }}
            >
                <div className="absolute inset-0 dot-grid opacity-30" />
                <div className="absolute inset-0 flex items-end p-5">
                    <div className="text-white">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md font-mono text-[9px] uppercase tracking-[0.18em] bg-white/15 backdrop-blur-sm border border-white/20">
                            {post.category}
                        </span>
                    </div>
                </div>
                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight size={16} strokeWidth={2} />
                </div>
            </div>
            <div className="p-6">
                <h3 className={`font-heading font-bold text-ink leading-snug tracking-tight ${featured ? "text-2xl" : "text-lg"} group-hover:text-brand-violet transition-colors`}>
                    {post.title}
                </h3>
                <p className="mt-3 text-sm text-muted-ink leading-relaxed line-clamp-3">
                    {post.excerpt}
                </p>
                <div className="mt-5 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.14em] text-faint-ink">
                    <span className="inline-flex items-center gap-1.5">
                        <Calendar size={11} strokeWidth={1.8} />
                        {new Date(post.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Clock size={11} strokeWidth={1.8} />
                        {post.readMins} min read
                    </span>
                </div>
            </div>
        </Link>
    );
}

export default function BlogList() {
    const [query, setQuery] = useState("");
    const [activeCat, setActiveCat] = useState("All");

    const cats = useMemo(() => ["All", ...Array.from(new Set(POSTS.map((p) => p.category)))], []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return POSTS.filter((p) => {
            if (activeCat !== "All" && p.category !== activeCat) return false;
            if (!q) return true;
            return (
                p.title.toLowerCase().includes(q) ||
                p.excerpt.toLowerCase().includes(q) ||
                p.tags.some((t) => t.toLowerCase().includes(q))
            );
        });
    }, [query, activeCat]);

    const featured = filtered[0];
    const rest = filtered.slice(1);

    // JSON-LD for the blog listing
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "DeCloudX Blog",
        "url": `${SITE.url}/blog`,
        "description": "Deep technical writing on decentralized cloud, DePIN, Layer-1 EVM, AI compute, and verifiable infrastructure.",
        "publisher": { "@type": "Organization", "name": "DeCloudX", "url": SITE.url },
        "blogPost": POSTS.slice(0, 12).map((p) => ({
            "@type": "BlogPosting",
            "headline": p.title,
            "url": `${SITE.url}/blog/${p.slug}`,
            "datePublished": p.publishedAt,
            "author": { "@type": "Organization", "name": p.author },
        })),
    };

    return (
        <div className="min-h-screen bg-page">
            <Seo
                title="Blog — Decentralized Cloud, DePIN, Layer-1 & AI Infrastructure"
                description="DeCloudX Blog — deep technical writing on hybrid-decentralized cloud infrastructure, DePIN, Layer-1 EVM settlement, distributed GPU compute, encrypted storage, and cloud sovereignty."
                keywords="DeCloudX blog, DePIN blog, decentralized cloud blog, Layer-1 EVM, AI compute, decentralized storage, hybrid cloud"
                path="/blog"
                jsonLd={jsonLd}
            />
            <Nav />

            <section className="relative pt-24 sm:pt-32 md:pt-40 pb-10 sm:pb-12 overflow-hidden">
                <div className="absolute inset-0 hero-glow-bg pointer-events-none" />
                <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-5 sm:px-6 md:px-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6"
                    >
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-2">
                            DeCloudX · Engineering Blog
                        </span>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-heading font-black text-[2rem] sm:text-[2.6rem] md:text-[3rem] lg:text-[3.4rem] leading-[1.05] tracking-[-0.025em] text-ink"
                    >
                        Building the cloud <br />
                        for <span className="brand-gradient-text">Internet 3.0</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-5 sm:mt-6 text-[15px] sm:text-base md:text-lg text-muted-ink max-w-2xl mx-auto leading-relaxed"
                    >
                        Deep technical writing on hybrid-decentralized cloud, DePIN, Layer-1 settlement, AI compute, encrypted storage, and the architecture of a sovereign internet.
                    </motion.p>

                    {/* Search + category filter */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-10 flex flex-col items-center gap-5"
                    >
                        <div className="relative w-full max-w-xl">
                            <Search size={15} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-faint-ink" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search articles, tags, authors…"
                                className="w-full pl-11 pr-4 py-3 rounded-xl surface-strong text-sm text-ink placeholder:text-faint-ink focus:outline-none focus:border-line-strong transition"
                                data-testid="blog-search"
                            />
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-2">
                            {cats.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setActiveCat(c)}
                                    className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.16em] border transition-all ${
                                        activeCat === c
                                            ? "bg-ink text-card border-ink"
                                            : "border-line text-muted-ink hover:text-ink hover:border-line-strong"
                                    }`}
                                    data-testid={`blog-cat-${c.toLowerCase()}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="pb-20 md:pb-32">
                <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
                    {filtered.length === 0 && (
                        <div className="text-center py-20">
                            <p className="text-muted-ink">No articles match your search.</p>
                        </div>
                    )}
                    {filtered.length > 0 && (
                        <>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
                                {featured && <PostCard post={featured} featured={true} />}
                                {rest.map((p) => <PostCard key={p.slug} post={p} />)}
                            </div>
                        </>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
}
